package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.domain.model.ComplaintStatus
import br.com.vvrefeicoes.portal.domain.model.CustomerComplaint
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import br.com.vvrefeicoes.portal.domain.repository.CustomerComplaintRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

class RemoteCustomerComplaintRepository(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val authRepository: AuthRepository,
) : CustomerComplaintRepository {
    override suspend fun load(period: MonthPeriod): List<CustomerComplaint> =
        get<OperationalComplaintsDto>("/operational/complaints?year=${period.year}&month=${period.month}")
            .items
            .map { it.toDomain() }

    override suspend fun upsert(record: CustomerComplaint) {
        val body = record.toRequest()
        if (isUuid(record.id)) {
            request { httpClient.put(baseUrl + "/operational/complaints/${record.id}") { jsonBody(body) } }
        } else {
            request { httpClient.post(baseUrl + "/operational/complaints") { jsonBody(body) } }
        }
    }

    override suspend fun delete(id: String) {
        request { httpClient.delete(baseUrl + "/operational/complaints/$id") { authorize() } }
    }

    private suspend inline fun <reified T> get(path: String): T = request { httpClient.get(baseUrl + path) { authorize() } }.body()

    private suspend inline fun request(
        crossinline call: suspend () -> HttpResponse,
    ): HttpResponse {
        val response = call()
        if (!response.status.isSuccess()) {
            val message =
                runCatching {
                    Json { ignoreUnknownKeys = true }
                        .decodeFromString<CustomerComplaintApiErrorDto>(response.bodyAsText())
                        .message
                }.getOrNull()
            error(message ?: "HTTP ${response.status.value}")
        }
        return response
    }

    private fun HttpRequestBuilder.authorize() {
        authRepository.session.value?.token?.let { token ->
            header(HttpHeaders.Authorization, "Bearer $token")
        }
    }

    private fun HttpRequestBuilder.jsonBody(body: Any) {
        authorize()
        contentType(ContentType.Application.Json)
        setBody(body)
    }

    private fun isUuid(value: String): Boolean = UUID_PATTERN.matches(value)

    private companion object {
        val UUID_PATTERN = Regex("[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}")
    }
}

@Serializable
private data class CustomerComplaintRequestDto(
    val organogramaUnidadeId: String,
    val data: String,
    val hora: String,
    val diretoria: String,
    val gestor: String,
    val restaurante: String,
    val servico: String,
    val status: String,
    val fornecedor: String,
    val turno: String,
    val natureza: String,
    val descricao: String,
    val tratativa: String,
    val responsavel: String,
)

@Serializable
private data class OperationalComplaintsDto(
    val items: List<CustomerComplaintDto> = emptyList(),
)

@Serializable
internal data class CustomerComplaintDto(
    val id: String,
    val organogramaUnidadeId: String = "",
    val data: String,
    val hora: String? = null,
    val diretoria: String? = null,
    val gestor: String? = null,
    val restaurante: String? = null,
    val servico: String? = null,
    val status: String = "OPEN",
    val fornecedor: String? = null,
    val turno: String? = null,
    val natureza: String? = null,
    val descricao: String? = null,
    val tratativa: String? = null,
    val responsavel: String? = null,
) {
    fun toDomain(): CustomerComplaint =
        CustomerComplaint(
            id = id,
            date = data,
            time = hora.orEmpty(),
            directorate = diretoria.orEmpty(),
            managerName = gestor.orEmpty(),
            restaurant = restaurante.orEmpty(),
            service = servico.orEmpty(),
            status = status.toComplaintStatus(),
            supplier = fornecedor.orEmpty(),
            shift = turno.orEmpty(),
            nature = natureza.orEmpty(),
            description = descricao.orEmpty(),
            treatment = tratativa.orEmpty(),
            treatmentOwner = responsavel.orEmpty(),
            unidadeId = organogramaUnidadeId,
        )
}

@Serializable
private data class CustomerComplaintApiErrorDto(
    val message: String? = null,
)

private fun CustomerComplaint.toRequest(): CustomerComplaintRequestDto =
    CustomerComplaintRequestDto(
        organogramaUnidadeId = unidadeId,
        data = date,
        hora = time,
        diretoria = directorate,
        gestor = managerName,
        restaurante = restaurant,
        servico = service,
        status = status.name,
        fornecedor = supplier,
        turno = shift,
        natureza = nature,
        descricao = description,
        tratativa = treatment,
        responsavel = treatmentOwner,
    )

private fun String.toComplaintStatus(): ComplaintStatus =
    when (uppercase()) {
        "IN_PROGRESS" -> ComplaintStatus.IN_PROGRESS
        "RESOLVED" -> ComplaintStatus.RESOLVED
        "CLOSED" -> ComplaintStatus.CLOSED
        else -> ComplaintStatus.OPEN
    }
